/**
 * Booking-specific service using simplified approach
 */
import { ServiceUtils, ServiceResponse, ServiceListResponse } from './BaseService';
import { supabase } from '@/integrations/supabase/client';

interface Booking {
  id: string;
  booking_id: string;
  property_id: string;
  property_short_name: string;
  guest_id: string | null;
  guest_name: string;
  guest_email: string | null;
  guest_phone: string | null;
  origin_id: string;
  check_in: string;
  check_out: string;
  nights: number;
  total_guests: number;
  children: number | null;
  total_payment: number;
  cleaning_fee: number | null;
  base_payment: number | null;
  pets_fee: number | null;
  early_checkin_fee: number | null;
  late_checkout_fee: number | null;
  other_fees: number | null;
  estimated_payout: number | null;
  stay_completed: boolean | null;
  paid: boolean | null;
  status: "Reservada" | "Cancelada";
  note: string | null;
  created_at: string;
  updated_at: string;
  origins?: {
    name: string;
  };
}

interface BookingFilters {
  property_id?: string;
  status?: string;
  check_in_from?: string;
  check_in_to?: string;
  guest_name?: string;
  paid?: boolean;
  stay_completed?: boolean;
}

export class BookingService {
  /**
   * Get bookings with filters
   */
  static async getBookings(filters?: BookingFilters): Promise<ServiceListResponse<Booking>> {
    try {
      let query = supabase
        .from('bookings')
        .select(`
          *,
          origins(name),
          guests(*)
        `)
        .order('check_in', { ascending: false });

      // Apply filters
      if (filters) {
        if (filters.property_id) {
          query = query.eq('property_id', filters.property_id);
        }
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.check_in_from) {
          query = query.gte('check_in', filters.check_in_from);
        }
        if (filters.check_in_to) {
          query = query.lte('check_in', filters.check_in_to);
        }
        if (filters.guest_name) {
          query = query.ilike('guest_name', `%${filters.guest_name}%`);
        }
        if (filters.paid !== undefined) {
          query = query.eq('paid', filters.paid);
        }
        if (filters.stay_completed !== undefined) {
          query = query.eq('stay_completed', filters.stay_completed);
        }
      }

      const { data, error, count } = await query;

      if (error) {
        return ServiceUtils.createListErrorResponse(
          ServiceUtils.handleError(error, 'Get bookings')
        );
      }

      ServiceUtils.logSuccess('Get bookings', { count, filters });
      return ServiceUtils.createListSuccessResponse(
        (data || []).map(booking => ({
          ...booking,
          status: booking.status as "Reservada" | "Cancelada"
        })), 
        count || 0
      );
    } catch (error) {
      return ServiceUtils.createListErrorResponse(
        ServiceUtils.handleError(error, 'Get bookings')
      );
    }
  }

  /**
   * Get booking by booking_id
   */
  static async getByBookingId(bookingId: string): Promise<ServiceResponse<Booking>> {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          origins(name),
          guests(*)
        `)
        .eq('booking_id', bookingId)
        .maybeSingle();

      if (error) {
        return ServiceUtils.createErrorResponse(
          ServiceUtils.handleError(error, `Get booking by booking ID: ${bookingId}`)
        );
      }

      ServiceUtils.logSuccess(`Get booking by booking ID: ${bookingId}`, data);
      return ServiceUtils.createSuccessResponse(data ? {
        ...data,
        status: data.status as "Reservada" | "Cancelada"
      } : null);
    } catch (error) {
      return ServiceUtils.createErrorResponse(
        ServiceUtils.handleError(error, `Get booking by booking ID: ${bookingId}`)
      );
    }
  }

  /**
   * Get upcoming bookings
   */
  static async getUpcoming(propertyId?: string): Promise<ServiceListResponse<Booking>> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      let query = supabase
        .from('bookings')
        .select(`
          *,
          origins(name)
        `)
        .gte('check_in', today)
        .order('check_in', { ascending: true });

      if (propertyId) {
        query = query.eq('property_id', propertyId);
      }

      const { data, error } = await query;

      if (error) {
        return ServiceUtils.createListErrorResponse(
          ServiceUtils.handleError(error, 'Get upcoming bookings')
        );
      }

      ServiceUtils.logSuccess('Get upcoming bookings', { count: data?.length });
      return ServiceUtils.createListSuccessResponse(
        (data || []).map(booking => ({
          ...booking,
          status: booking.status as "Reservada" | "Cancelada"
        }))
      );
    } catch (error) {
      return ServiceUtils.createListErrorResponse(
        ServiceUtils.handleError(error, 'Get upcoming bookings')
      );
    }
  }

  /**
   * Creates a booking request
   */
  static async createBookingRequest(data: {
    property_title: string;
    full_name: string;
    email: string;
    phone: string;
    check_in: string;
    check_out: string;
    nights: number;
    total_price: number;
    status: 'pending';
  }): Promise<ServiceResponse<null>> {
    try {
      console.log('[BookingService] Inserting booking request:', data);
      
      const { error } = await supabase
        .from('booking_requests')
        .insert(data);

      if (error) {
        console.error('[BookingService] Supabase error:', error);
        return ServiceUtils.createErrorResponse(
          ServiceUtils.handleError(error, 'Create booking request')
        );
      }

      console.log('[BookingService] Insert successful');
      ServiceUtils.logSuccess('Create booking request', null);
      return ServiceUtils.createSuccessResponse(null);
    } catch (error) {
      console.error('[BookingService] Unexpected error:', error);
      return ServiceUtils.createErrorResponse(
        ServiceUtils.handleError(error, 'Create booking request')
      );
    }
  }

  /**
   * Gets booking requests with optional filters
   */
  static async getBookingRequests(filters?: {
    propertyTitle?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ServiceListResponse<any>> {
    try {
      let query = supabase
        .from('booking_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.propertyTitle) {
        query = query.ilike('property_title', `%${filters.propertyTitle}%`);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.dateFrom) {
        query = query.gte('check_in', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('check_out', filters.dateTo);
      }

      const { data, error, count } = await query;

      if (error) {
        return ServiceUtils.createListErrorResponse(
          ServiceUtils.handleError(error, 'Get booking requests')
        );
      }

      ServiceUtils.logSuccess('Get booking requests', { count: data?.length });
      return ServiceUtils.createListSuccessResponse(data || [], count || undefined);
    } catch (error) {
      return ServiceUtils.createListErrorResponse(
        ServiceUtils.handleError(error, 'Get booking requests')
      );
    }
  }
}

export const bookingService = BookingService;
export type { Booking, BookingFilters };